import React from "react";
import { ActionPrompt, ActionPromptTag } from "./actionPromptTypes";

interface ActionPromptCardProps {
  prompt: ActionPrompt;
  onEdit: (prompt: ActionPrompt) => void;
  onDelete: (prompt: ActionPrompt) => void;
  onToggleEnabled: (prompt: ActionPrompt) => void;
}

export function ActionPromptCard({
  prompt,
  onEdit,
  onDelete,
  onToggleEnabled,
}: ActionPromptCardProps) {
  // Group tags by group code
  const genderTags = (prompt.tags || []).filter(
    (t) => t.groupCode === "gender" || t.code === "male" || t.code === "female"
  );
  const ageTags = (prompt.tags || []).filter(
    (t) =>
      t.groupCode === "age_group" ||
      ["child", "young", "middle_aged", "senior"].includes(t.code)
  );
  const customTags = (prompt.tags || []).filter(
    (t) => !genderTags.some((g) => g.id === t.id) && !ageTags.some((a) => a.id === t.id)
  );

  return (
    <div
      className={`bg-white rounded-xl border transition-all duration-200 flex flex-col justify-between overflow-hidden shadow-sm hover:shadow-md ${
        prompt.enabled ? "border-gray-200 hover:border-teal-300" : "border-gray-200/70 opacity-75 bg-gray-50/40"
      }`}
    >
      <div className="p-4 space-y-3">
        {/* Header: Name, SortOrder, TemplateCount */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-gray-900 truncate" title={prompt.name}>
                {prompt.name}
              </h3>
              {!prompt.enabled && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-700 border border-red-200 shrink-0">
                  已禁用
                </span>
              )}
            </div>
          </div>
          {/* Template count badge */}
          <span
            className={`px-2 py-0.5 rounded text-xs font-medium shrink-0 flex items-center gap-1 ${
              prompt.templateCount > 0
                ? "bg-teal-50 text-teal-700 border border-teal-200"
                : "bg-gray-50 text-gray-500 border border-gray-200"
            }`}
            title={`已被 ${prompt.templateCount} 个模板关联为适用动作`}
          >
            <i className="ri-layout-grid-line text-xs" />
            <span>{prompt.templateCount} 个模板</span>
          </span>
        </div>

        {/* Prompt text content */}
        <div className="relative bg-slate-50 border border-slate-100 rounded-lg p-2.5 text-xs text-slate-700 leading-relaxed font-mono break-words line-clamp-3 select-text">
          {prompt.text}
        </div>

        {/* Tag chips */}
        <div className="space-y-1.5 pt-1">
          <div className="flex flex-wrap gap-1.5 text-xs">
            {/* Gender tags */}
            {genderTags.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200">
                <i className="ri-women-line text-[10px]" />
                <span>性别: {genderTags.map((t) => t.name).join("/")}</span>
              </span>
            )}

            {/* Age tags */}
            {ageTags.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-50 text-purple-700 border border-purple-200">
                <i className="ri-user-heart-line text-[10px]" />
                <span>年龄: {ageTags.map((t) => t.name).join("/")}</span>
              </span>
            )}

            {/* Custom tags */}
            {customTags.map((t) => (
              <span
                key={t.id}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200"
              >
                {t.name}
              </span>
            ))}

            {(prompt.tags || []).length === 0 && (
              <span className="text-gray-400 text-[11px] italic">未配置标签</span>
            )}
          </div>
        </div>
      </div>

      {/* Footer: Toggle, Edit, Delete */}
      <div className="px-4 py-2.5 bg-gray-50/70 border-t border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <label
            className="relative inline-flex items-center cursor-pointer"
            title={prompt.enabled ? "点击禁用" : "点击启用"}
          >
            <input
              type="checkbox"
              checked={prompt.enabled}
              className="sr-only peer"
              onChange={() => onToggleEnabled(prompt)}
            />
            <div className="w-8 h-4 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-teal-600" />
          </label>
          <span className="text-xs text-gray-500 font-medium">
            {prompt.enabled ? "已启用" : "已禁用"}
          </span>
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onEdit(prompt)}
            className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
            title="编辑动作提示词"
          >
            <i className="ri-edit-line text-sm" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(prompt)}
            className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="删除动作提示词"
          >
            <i className="ri-delete-bin-line text-sm" />
          </button>
        </div>
      </div>
    </div>
  );
}
